import { createDictationController } from './index.js';
import { createTranscriptState } from './transcript-state.js';
import { buildRecognitionContext } from './recognition-context.js';

const busyStates = new Set(['requesting_permission', 'connecting', 'listening', 'finalizing']);

// Drafts belong to this mounted workspace only. They never enter chat persistence.
export default {
  data() {
    return {
      dictationEnabled: window.lexiusChatDictationEnabled === true,
      composerSendInFlight: false,
      dictationSendPending: false,
      composerUnsavedDrafts: [],
      dictationEditorBusy: false,
      dictationState: 'idle',
      dictationError: null,
      dictationRecovery: '',
      dictationCopied: false,
      dictationCallActive: window.lexiusCallMicrophoneActive === true,
      composerSelection: { start: 0, end: 0 },
    };
  },
  created() {
    this._composerDrafts = new Map();
    this._composerIdentities = new WeakMap();
    this._composerUuidIdentities = new Map();
    this._dictationGeneration = 0;
    this._composerIdentity = this.composerIdentityFor(this.currentSession);
    this._composerSession = this.currentSession;
    this._composerDraftSerial = 0;
  },
  mounted() {
    document.addEventListener('visibilitychange', this.onDictationVisibility);
    window.addEventListener('pagehide', this.cancelDictation);
    window.addEventListener('lexius:logout', this.onDictationLogout);
    window.addEventListener('lexius:microphone-call', this.onDictationCall);
    window.addEventListener('lexius:dictation-editor-open', this.cancelDictation);
    window.addEventListener('lexius:dictation-editor-state', this.onDictationEditorState);
    this._dictationModal = document.getElementById('chatModal');
    this._dictationModal?.addEventListener('hide.bs.modal', this.cancelDictation);
  },
  beforeUnmount() {
    this.cancelDictation();
    document.removeEventListener('visibilitychange', this.onDictationVisibility);
    window.removeEventListener('pagehide', this.cancelDictation);
    window.removeEventListener('lexius:logout', this.onDictationLogout);
    window.removeEventListener('lexius:microphone-call', this.onDictationCall);
    window.removeEventListener('lexius:dictation-editor-open', this.cancelDictation);
    window.removeEventListener('lexius:dictation-editor-state', this.onDictationEditorState);
    this._dictationModal?.removeEventListener('hide.bs.modal', this.cancelDictation);
    this._composerDrafts.clear();
  },
  computed: {
    dictationBusy() { return busyStates.has(this.dictationState); },
    dictationStarting() {
      return this.dictationState === 'requesting_permission' || this.dictationState === 'connecting';
    },
    dictationUnavailable() {
      return this.dictationCallActive || this.dictationEditorBusy || this.composerSendInFlight || this.contextResumeBusy || this.reviewIntentChecking
        || this.activeReviewSubmitChecking || this.currentSession?.is_call_log;
    },
    dictationStatusText() {
      if (this.dictationError) return this.dictationError.message;
      if (this.dictationSendPending) return 'Terminando dictado y enviando…';
      if (this.dictationState === 'requesting_permission') return 'Permite el micrófono en el aviso del navegador. Puedes cancelar mientras esperas.';
      if (this.dictationState === 'connecting') return 'Conectando el dictado… Espera para hablar.';
      if (this.dictationState === 'listening') return 'Escuchando… ya puedes hablar.';
      if (this.dictationState === 'review') return 'Dictado detenido. Revisa el texto de la transcripción.';
      if (this.dictationState === 'finalizing') return 'Terminando el último fragmento…';
      if (this.dictationCallActive) return 'El dictado estará disponible cuando termine la llamada.';
      return '';
    },
    composerSendLabel() {
      if (this.dictationSendPending) return 'Enviando dictado…';
      if (this.dictationBusy && !this.dictationStarting) return 'Detener dictado y enviar mensaje';
      return 'Enviar mensaje';
    },
  },
  watch: {
    currentSession: {
      flush: 'sync',
      handler(session) {
        if (!this._composerDrafts) return;
        const identity = this.composerIdentityFor(session);
        if (identity === this._composerIdentity) {
          this._composerSession = session;
          return;
        }
        this.saveComposerDraft();
        this.cancelDictation();
        this._composerIdentity = identity;
        this._composerSession = session;
        const draft = this._composerDrafts.get(identity);
        this._dictationWriting = true;
        this.message = draft?.text || '';
        this._dictationWriting = false;
        this.composerSelection = draft?.selection || { start: 0, end: 0 };
        this.dictationRecovery = draft?.recovery || '';
        this.dictationCopied = false;
        this.saveComposerDraft();
        this.$nextTick(() => {
          this.restoreComposerSelection();
          this.adjustTextareaHeightAndCheckLength(undefined, { dictation: true });
        });
      },
    },
    'currentSession.uuid': {
      flush: 'sync',
      handler() {
        // Assigning the first server UUID must retain the unsaved draft identity.
        if (this._composerDrafts) this.composerIdentityFor(this.currentSession);
      },
    },
    message: {
      flush: 'sync',
      handler() {
        if (!this._composerDrafts) return;
        if (!this._dictationWriting) this.pauseDictationForEdit();
        if (this.dictationState === 'review' && !String(this.message || '').trim()) this.dictationState = 'idle';
        this.saveComposerDraft();
      },
    },
  },
  methods: {
    composerIdentityFor(session) {
      if (!session || typeof session !== 'object') return null;
      let identity = this._composerIdentities.get(session);
      const uuid = String(session.uuid || '');
      if (!identity) identity = (uuid && this._composerUuidIdentities.get(uuid)) || Symbol('chat-draft');
      this._composerIdentities.set(session, identity);
      if (uuid) this._composerUuidIdentities.set(uuid, identity);
      return identity;
    },
    saveComposerDraft() {
      if (!this._composerDrafts || !this._composerIdentity) return;
      const text = String(this.message || '');
      const selection = {
        start: Math.min(this.composerSelection.start, text.length),
        end: Math.min(this.composerSelection.end, text.length),
      };
      const prior = this._composerDrafts.get(this._composerIdentity);
      this._composerDrafts.set(this._composerIdentity, {
        text, selection, recovery: this.dictationRecovery,
        // Saved chats can be reloaded by UUID; retain full context only for
        // unsaved chats which have no server-backed restoration path yet.
        session: this._composerSession?.uuid ? null : this._composerSession,
        id: prior?.id || ++this._composerDraftSerial,
      });
      this.composerUnsavedDrafts = [...this._composerDrafts.entries()]
        .filter(([identity, draft]) => identity !== this._composerIdentity && draft.session && !draft.session.uuid && draft.text.trim())
        .map(([, draft]) => ({ id: draft.id, label: draft.text.replace(/\s+/g, ' ').slice(0, 60) }));
    },
    async restoreUnsavedComposerDraft(event) {
      const id = Number(event.target.value);
      const draft = [...this._composerDrafts.values()].find(item => item.id === id && !item.session?.uuid);
      if (!draft?.session) return;
      await this.switchSession(null, null, null, null, null, { draftSession: draft.session });
      this.$emit('sessionActivated', this.currentSession?.uuid || null);
      event.target.value = '';
    },
    rememberComposerSelection(event) {
      const input = event?.target || this.$refs.messageInput;
      if (!input || typeof input.selectionEnd !== 'number') return;
      this.composerSelection = { start: input.selectionStart, end: input.selectionEnd };
      this.saveComposerDraft();
    },
    restoreComposerSelection() {
      this.$refs.messageInput?.setSelectionRange(this.composerSelection.start, this.composerSelection.end);
    },
    pauseDictationForEdit() {
      if (this.dictationSendPending) { this.cancelDictation(); return; }
      if (!this._dictationTranscript || !this.dictationBusy) return;
      this._dictationTranscript.freeze();
      this._dictationController?.freezeForEdit();
    },
    onComposerInput(event) {
      // beforeinput handles native edits; this also covers browser mutations
      // which emit input without beforeinput and competing programmatic writers.
      this.pauseDictationForEdit();
      this.rememberComposerSelection(event);
      this.adjustTextareaHeightAndCheckLength(event);
    },
    async finishDictationForSend() {
      const controller = this._dictationController;
      const transcript = this._dictationTranscript;
      if (!controller || this.dictationStarting) return false;
      if (transcript?.snapshot().frozen) {
        // The user already corrected this draft; late speech belongs only to
        // recovery and must not delay or replace the text they chose to send.
        this.cancelDictation();
        return true;
      }
      const generation = this._dictationGeneration;
      const identity = this._composerIdentity;
      this.dictationSendPending = true;
      try {
        // Stop releases the microphone synchronously, then flushes the last
        // audio and applies final wording before the send snapshots the draft.
        await controller.stop();
        return generation === this._dictationGeneration
          && identity === this._composerIdentity
          && controller === this._dictationController
          && !transcript?.snapshot().frozen
          && this.dictationState === 'review';
      } finally {
        this.dictationSendPending = false;
      }
    },
    toggleDictation() {
      if (this.dictationStarting) { this.cancelDictation(); return; }
      if (this.dictationState === 'listening') { this._dictationController?.stop(); return; }
      if (this.dictationBusy || !this.dictationEnabled || this.dictationUnavailable) return;
      this.rememberComposerSelection();
      this._dictationController?.dispose();
      const generation = ++this._dictationGeneration;
      const identity = this._composerIdentity;
      const transcript = createTranscriptState(String(this.message || ''), this.composerSelection.end);
      this._dictationTranscript = transcript;
      this.dictationError = null;
      const isCurrent = () => generation === this._dictationGeneration && identity === this._composerIdentity;
      const receive = (kind, event) => {
        if (!isCurrent()) return;
        if (event.editable === false) transcript.freeze();
        const snapshot = transcript[kind](event.text);
        if (snapshot.frozen) {
          if (snapshot.recovery) {
            this.dictationRecovery = snapshot.recovery;
            this.dictationCopied = false;
          }
          this.saveComposerDraft();
          return;
        }
        this._dictationWriting = true;
        this.message = snapshot.text;
        this._dictationWriting = false;
        this.composerSelection = { start: snapshot.caret, end: snapshot.caret };
        this.saveComposerDraft();
        this.$nextTick(() => {
          if (!isCurrent() || transcript.snapshot().frozen) return;
          this.restoreComposerSelection();
          this.adjustTextareaHeightAndCheckLength(undefined, { dictation: true });
        });
      };
      this._dictationController = createDictationController({
        onState: ({ state, error }) => {
          if (!isCurrent()) return;
          this.dictationState = state;
          this.dictationError = error || null;
        },
        onPartial: event => receive('partial', event),
        onCommitted: event => receive('commit', event),
        // Only fixed event names/reasons leave the controller. No content or URLs.
        onMetric: metric => {
          if (!isCurrent()) return;
          window.dispatchEvent(new CustomEvent('lexius:dictation-metric', { detail: metric }));
          if (['connection_failure', 'finalization_timeout'].includes(metric.name)) {
            window.Sentry?.captureMessage?.(`chat_dictation.${metric.name}`, {
              level: 'warning', tags: { dictation_reason: metric.reason || 'final_commit' },
            });
          }
        },
      });
      this.hideMentionSuggestions();
      this._dictationController.start(buildRecognitionContext(this.message, this.composerSelection.end, window.country));
    },
    cancelDictation() {
      this._dictationGeneration = (this._dictationGeneration || 0) + 1;
      this.dictationSendPending = false;
      this._dictationTranscript?.freeze();
      this._dictationController?.dispose();
      this._dictationController = null;
      this._dictationTranscript = null;
      this.dictationState = 'idle';
      this.dictationError = null;
      this.saveComposerDraft();
    },
    onDictationVisibility() { if (document.hidden) this.cancelDictation(); },
    onDictationCall(event) {
      this.dictationCallActive = event.detail?.active === true;
      if (this.dictationCallActive) this.cancelDictation();
    },
    onDictationEditorState(event) { this.dictationEditorBusy = event.detail?.active === true; },
    onDictationLogout() {
      this.cancelDictation();
      this._composerDrafts.clear();
      this._composerUuidIdentities.clear();
      this.message = '';
      this.dictationRecovery = '';
      this.dictationEnabled = false;
      this._composerDrafts.clear();
      this.composerUnsavedDrafts = [];
    },
    async copyDictationRecovery() {
      try {
        await navigator.clipboard.writeText(this.dictationRecovery);
        this.dictationCopied = true;
      } catch {
        this.dictationCopied = false;
      }
    },
  },
};
